import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Security as SecurityIcon,
  InfoOutlined as InfoIcon,
  SecurityOutlined as ShieldIcon,
  PeopleOutlined as PeopleIcon,
  HistoryOutlined as HistoryIcon,
  Business as BusinessIcon,
  CategoryOutlined as CategoryIcon,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import RoleBasicInfoSection from './RoleManagement/components/RoleBasicInfoSection';
import PermissionsSection from '@/components/PermissionsSection/PermissionsSection';
import RoleCustomPaymentTypeAccessSection from './RoleManagement/components/RoleCustomPaymentTypeAccessSection';
import RoleUsersSection from './RoleManagement/components/RoleUsersSection';
import RoleHistorySection from './RoleManagement/components/RoleHistorySection';
import { useGetRole } from '@/api/hooks/roles/useGetRole';
import { useUpdateRole } from '@/api/hooks/roles/useUpdateRole';
import { useGetRoleCustomPaymentTypeAccess } from '@/api/hooks/roles/useGetRoleCustomPaymentTypeAccess';
import { useUpdateRoleCustomPaymentTypeAccess } from '@/api/hooks/roles/useUpdateRoleCustomPaymentTypeAccess';
import { useConfirmation } from '@/context/ConfirmationContext';
import CpdActionButton from '@/components/ui/CpdActionButton';

const ROLE_DETAILS_TABS = {
  BASIC_INFO: 0,
  ORGANIZATION_PERMISSIONS: 1,
  FACILITY_PERMISSIONS: 2,
  CUSTOM_PAYMENT_TYPE_ACCESS: 3,
  ASSIGNED_USERS: 4,
  HISTORY: 5,
};

const RoleDetails = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const basicInfoRef = useRef(null);
  const { requestConfirmation } = useConfirmation();

  const { role, loading, error: fetchError, refetch } = useGetRole(roleId);
  const {
    updateRole,
    loading: updateLoading,
    error: updateError,
  } = useUpdateRole();
  const {
    customPaymentTypeAccess,
    loading: customPaymentTypeAccessLoading,
    error: customPaymentTypeAccessError,
    refetch: refetchCustomPaymentTypeAccess,
  } = useGetRoleCustomPaymentTypeAccess(roleId);
  const {
    updateRoleCustomPaymentTypeAccess,
    loading: customPaymentTypeAccessUpdateLoading,
    error: customPaymentTypeAccessUpdateError,
  } = useUpdateRoleCustomPaymentTypeAccess();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl ? parseInt(tabFromUrl, 10) : 0;
  });
  const [basicInfo, setBasicInfo] = useState({});
  const [facilityPermissions, setFacilityPermissions] = useState({});
  const [organizationPermissions, setOrganizationPermissions] = useState({});
  const [
    customPaymentTypeAccessForm,
    setCustomPaymentTypeAccessForm,
  ] = useState(null);

  useEffect(() => {
    if (role) {
      setFacilityPermissions(role.facility_permissions || {});
      setOrganizationPermissions(role.organization_permissions || {});
    }
  }, [role]);

  useEffect(() => {
    if (customPaymentTypeAccess) {
      setCustomPaymentTypeAccessForm(customPaymentTypeAccess);
    }
  }, [customPaymentTypeAccess]);

  const handleBasicInfoChange = (newBasicInfo) => {
    setBasicInfo(newBasicInfo);
  };

  const handleFacilityPermissionChange = (newPermissions) => {
    setFacilityPermissions(newPermissions);
  };

  const handleOrganizationPermissionChange = (newPermissions) => {
    setOrganizationPermissions(newPermissions);
  };

  const handleCustomPaymentTypeAccessChange = (newAccess) => {
    setCustomPaymentTypeAccessForm(newAccess);
  };

  const handleApplyTemplate = (template) => {
    if (template.facilityPermissions) {
      setFacilityPermissions(template.facilityPermissions);
    }
    if (template.organizationPermissions) {
      setOrganizationPermissions(template.organizationPermissions);
    }
    if (template.permissions && !template.facilityPermissions) {
      setFacilityPermissions(template.permissions);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', newValue.toString());
    }
    setSearchParams(newSearchParams);
  };

  const handleSave = async () => {
    setWarningMessage('');

    const originalSelectedIds = [
      ...(customPaymentTypeAccess?.selected_custom_payment_type_ids || []),
    ].sort((left, right) => left - right);
    const nextSelectedIds = [
      ...(customPaymentTypeAccessForm?.selected_custom_payment_type_ids || []),
    ].sort((left, right) => left - right);
    const hasCustomPaymentTypeAccessChanges =
      customPaymentTypeAccess?.custom_payment_type_access_enabled !==
        customPaymentTypeAccessForm?.custom_payment_type_access_enabled ||
      originalSelectedIds.length !== nextSelectedIds.length ||
      originalSelectedIds.some((id, index) => id !== nextSelectedIds[index]);

    if (
      activeTab === ROLE_DETAILS_TABS.CUSTOM_PAYMENT_TYPE_ACCESS &&
      role.assigned_users_count > 0 &&
      hasCustomPaymentTypeAccessChanges
    ) {
      requestConfirmation(
        `This role is assigned to ${role.assigned_users_count} user(s). Saving these custom payment type access changes may reduce their payment visibility and will sign them out. Continue?`,
        () => performSave(),
      );
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    try {
      let formData;
      let successMsg;
      let updatedAccess;
      const organizationChanged =
        activeTab === ROLE_DETAILS_TABS.BASIC_INFO &&
        Number(basicInfo.organizationId) !== Number(role.organization_id);

      if (activeTab === ROLE_DETAILS_TABS.BASIC_INFO) {
        if (basicInfoRef.current && basicInfoRef.current.validateForm()) {
          formData = {
            name: basicInfo.name,
            description: basicInfo.description,
            organization_id: basicInfo.organizationId,
            is_default: basicInfo.isDefault,
            has_all_facilities_access: basicInfo.hasAllFacilitiesAccess,
            facility_permissions: role.facility_permissions,
            organization_permissions: role.organization_permissions,
          };
          successMsg = 'Basic information updated successfully';
        } else {
          return;
        }
      } else if (activeTab === ROLE_DETAILS_TABS.ORGANIZATION_PERMISSIONS) {
        formData = {
          name: role.name,
          description: role.description,
          organization_id: role.organization_id,
          facility_permissions: role.facility_permissions,
          organization_permissions: organizationPermissions,
        };
        successMsg = 'Organization permissions updated successfully';
      } else if (activeTab === ROLE_DETAILS_TABS.FACILITY_PERMISSIONS) {
        formData = {
          name: role.name,
          description: role.description,
          organization_id: role.organization_id,
          facility_permissions: facilityPermissions,
          organization_permissions: role.organization_permissions,
        };
        successMsg = 'Facility permissions updated successfully';
      } else if (
        activeTab === ROLE_DETAILS_TABS.CUSTOM_PAYMENT_TYPE_ACCESS &&
        customPaymentTypeAccessForm
      ) {
        await updateRoleCustomPaymentTypeAccess(roleId, {
          custom_payment_type_access_enabled:
            customPaymentTypeAccessForm.custom_payment_type_access_enabled,
          custom_payment_type_ids:
            customPaymentTypeAccessForm.selected_custom_payment_type_ids || [],
        });

        updatedAccess = await refetchCustomPaymentTypeAccess();

        successMsg = 'Custom payment type access updated successfully';
      }

      if (formData) {
        await updateRole(roleId, formData);

        await refetch();
        updatedAccess = await refetchCustomPaymentTypeAccess();
      }

      if (
        organizationChanged &&
        updatedAccess?.custom_payment_type_access_enabled &&
        (updatedAccess.selected_custom_payment_type_ids || []).length === 0
      ) {
        setWarningMessage(
          'Organization changed. Custom payment type access is still enabled, and this role will hide all custom-payment-type records until you select allowed types for the new organization.',
        );
      }

      if (successMsg) {
        setSuccessMessage(successMsg);
        setShowSuccessAlert(true);

        setTimeout(() => {
          const successAlert = document.getElementById('success-alert');
          if (successAlert) {
            successAlert.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  const handleBack = () => {
    navigate('/admin/roles');
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  };

  if (loading) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (fetchError) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='error' sx={{ m: 2 }}>
          {fetchError}
        </Alert>
      </PageLayout>
    );
  }

  if (!role) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <SecurityIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                Role Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='warning' sx={{ m: 2 }}>
          Role not found
        </Alert>
      </PageLayout>
    );
  }

  const saveLoading =
    activeTab === ROLE_DETAILS_TABS.CUSTOM_PAYMENT_TYPE_ACCESS
      ? customPaymentTypeAccessUpdateLoading
      : updateLoading;

  const isFormDisabled =
    saveLoading ||
    (activeTab === ROLE_DETAILS_TABS.BASIC_INFO &&
      !basicInfoRef.current?.isFormValid) ||
    (activeTab === ROLE_DETAILS_TABS.CUSTOM_PAYMENT_TYPE_ACCESS &&
      (customPaymentTypeAccessLoading || !customPaymentTypeAccessForm));

  const activeTabError = updateError || customPaymentTypeAccessUpdateError;

  return (
    <PageLayout
      height='auto'
      padding={{ pt: 2, px: 2, pb: 4 }}
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
            <SecurityIcon sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              Role Details: {role.name}
            </Typography>
          </Box>
        </CardHeader>
      }>
      {showSuccessAlert && successMessage && (
        <Alert
          id='success-alert'
          severity='success'
          sx={{ mb: 3 }}
          onClose={handleSuccessClose}>
          {successMessage}
        </Alert>
      )}

      {warningMessage && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          {warningMessage}
        </Alert>
      )}

      {activeTabError && (
        <Alert id='error-alert' severity='error' sx={{ mb: 3 }}>
          {activeTabError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'>
          <Tab
            icon={<InfoIcon />}
            iconPosition='start'
            label='Basic Information'
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition='start'
            label='Organization Permissions'
          />
          <Tab
            icon={<ShieldIcon />}
            iconPosition='start'
            label='Facility Permissions'
          />
          <Tab
            icon={<CategoryIcon />}
            iconPosition='start'
            label='Custom Payment Types'
          />
          <Tab
            icon={
              <Badge
                badgeContent={role?.assigned_users_count || 0}
                color='info'
                showZero
                sx={{
                  '& .MuiBadge-badge': {
                    color: 'white',
                    fontWeight: 'bold',
                  },
                }}>
                <PeopleIcon />
              </Badge>
            }
            iconPosition='start'
            label='Assigned Users'
          />
          <Tab icon={<HistoryIcon />} iconPosition='start' label='History' />
        </Tabs>
      </Box>

      {activeTab === ROLE_DETAILS_TABS.BASIC_INFO && (
        <RoleBasicInfoSection
          ref={basicInfoRef}
          role={role}
          onFormChange={handleBasicInfoChange}
        />
      )}

      {activeTab === ROLE_DETAILS_TABS.ORGANIZATION_PERMISSIONS && (
        <PermissionsSection
          permissions={organizationPermissions}
          onPermissionChange={handleOrganizationPermissionChange}
          onApplyTemplate={handleApplyTemplate}
          permissionLevel='organization'
        />
      )}

      {activeTab === ROLE_DETAILS_TABS.FACILITY_PERMISSIONS && (
        <PermissionsSection
          permissions={facilityPermissions}
          onPermissionChange={handleFacilityPermissionChange}
          onApplyTemplate={handleApplyTemplate}
          permissionLevel='facility'
        />
      )}

      {activeTab === ROLE_DETAILS_TABS.CUSTOM_PAYMENT_TYPE_ACCESS && (
        <RoleCustomPaymentTypeAccessSection
          roleOrganizationId={role.organization_id}
          customPaymentTypeAccess={customPaymentTypeAccessForm}
          loading={customPaymentTypeAccessLoading}
          error={customPaymentTypeAccessError}
          onChange={handleCustomPaymentTypeAccessChange}
        />
      )}

      {activeTab === ROLE_DETAILS_TABS.ASSIGNED_USERS && (
        <RoleUsersSection roleId={roleId} />
      )}

      {activeTab === ROLE_DETAILS_TABS.HISTORY && (
        <RoleHistorySection roleId={roleId} />
      )}

      {activeTab < ROLE_DETAILS_TABS.ASSIGNED_USERS && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
          <CpdActionButton
            onClick={handleSave}
            loading={saveLoading}
            startIcon={<SaveIcon sx={{ color: 'white' }} />}
            disabled={isFormDisabled}>
            {saveLoading ? 'Saving...' : 'Save Changes'}
          </CpdActionButton>
        </Box>
      )}
    </PageLayout>
  );
};

export default RoleDetails;
